<script setup lang="ts">
import emblaCarouselVue from 'embla-carousel-vue';

import type { Media } from '~/packages/commerce/types/product';

withDefaults(
    defineProps<{
        images?: Media[];
        hasImagePreview?: boolean;
        hasAspectVideo?: boolean;
        hasThumbnails?: boolean;
        hasArrows?: boolean;
        hasDots?: boolean;
        isLoading?: boolean;
        loading: 'lazy' | 'eager';
        width: number;
    }>(),
    {
        hasArrows: false,
        hasAspectVideo: false,
        hasDots: false,
        hasThumbnails: false,
        loading: 'lazy',
        width: 80,
    },
);

const [emblaRef, emblaApi] = emblaCarouselVue();

const amountOfSlides = ref(0);
const currentSlideIndex = ref(0);
const canScrollPrevious = ref(false);
const canScrollNext = ref(true);

onMounted(() => {
    emblaApi.value?.on('select', () => {
        currentSlideIndex.value = emblaApi.value.selectedScrollSnap();
        canScrollPrevious.value = emblaApi.value.canScrollPrev();
        canScrollNext.value = emblaApi.value.canScrollNext();
    });

    amountOfSlides.value = emblaApi.value?.slideNodes()?.length;
});
</script>

<template>
    <section class=" w-full">
        <div
            ref="emblaRef"
            class="embla__viewport  overflow-x-hidden"
        >
            <div
                :class="`embla__container grid grid-flow-col touch-pan-y ${
                    width < 100
                        ? 'ml-dynamic mr-dynamic gap-dynamic'
                        : ''
                }`"
                :style="`grid-auto-columns: ${width}%;`"
            >
                <CommBaseSlide
                    v-for="image in images"
                    :key="image.url"
                    :class="`border border-gray-200 rounded overflow-hidden
                        ${hasAspectVideo && 'aspect-video'}
                    `"
                >
                    <CommBaseModal
                        v-if="hasImagePreview"
                        additional-classes="p-5"
                    >
                        <template #trigger>
                            <NuxtImg
                                :loading="loading"
                                format="webp"
                                width="800"
                                height="500"
                                :alt="image.alt"
                                :src="image.url"
                                decoding="auto"
                                class="cursor-zoom-in"
                            />
                        </template>
                        <NuxtImg
                            loading="lazy"
                            format="webp"
                            width="1280"
                            height="720"
                            :alt="image.alt"
                            :src="image.url"
                            decoding="auto"
                        />
                    </CommBaseModal>
                    <NuxtImg
                        v-else
                        :loading="loading"
                        format="webp"
                        :alt="image.alt"
                        :src="image.url"
                        decoding="auto"
                    />
                </CommBaseSlide>
            </div>
        </div>
        <Transition
            enter-active-class="transition-opacity duration-100 ease-[cubic-bezier(.75,.11,.85,.01)]"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition-opacity duration-300 ease-[cubic-bezier(.25,.89,.15,.99)]"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <BaseText
                v-if="images[currentSlideIndex]?.description"
                class="mx-dynamic mt-3 flex justify-center"
                color="text-gray-600"
            >
                {{ images[currentSlideIndex]?.description }}
            </BaseText>
        </Transition>
        <div
            class="mx-dynamic mt-4 grid items-center"
            :class="[hasArrows ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-1']"
        >
            <template v-if="hasArrows && !isLoading && images?.length > 0">
                <button
                    :class="`p-3 border border-gray-900 rounded transition-opacity ${
                        !canScrollPrevious && 'opacity-0'
                    }`"
                    aria-label="Vorheriges Bild anzeigen"
                    :disabled="!canScrollPrevious"
                    @click="emblaApi?.scrollPrev"
                >
                    <JamBaseIcon icon-name="angle-left" />
                </button>
            </template>
            <div
                v-if="hasDots && amountOfSlides"
                class="flex justify-center gap-3"
            >
                <span
                    v-for="(slide, index) in amountOfSlides"
                    :key="`dot-${slide}`"
                    class="h-2 !rounded-full bg-gray-900 transition-all"
                    :class="[currentSlideIndex === index ? 'w-10' : 'w-2']"
                />
            </div>
            <template v-if="hasArrows && !isLoading && images?.length > 0">
                <button
                    :class="`p-3 border border-gray-900 rounded transition-opacity ${
                        !canScrollNext && 'opacity-0'
                    }`"
                    :disabled="!canScrollNext"
                    aria-label="Nächstes Bild anzeigen"
                    @click="emblaApi?.scrollNext"
                >
                    <JamBaseIcon icon-name="angle-right" />
                </button>
            </template>
        </div>
        <div
            v-if="hasThumbnails"
            ref="thumbnails"
            class="mt-5 flex gap-3 overflow-hidden"
        >
            <button
                v-for="(image, index) in images"
                :key="image.url"
                class="rounded-md border border-transparent"
                :class="[
                    index === currentSlideIndex && 'border !border-gray-900',
                ]"
                @click="emblaApi?.scrollTo(index)"
            >
                <NuxtImg
                    :loading="loading"
                    format="webp"
                    height="200"
                    width="200"
                    :src="image.url"
                    class="!h-[75px] !w-[120px] rounded-md object-cover object-top"
                    :alt="`Vorschaubild: ${image.alt}`"
                    decoding="auto"
                />
            </button>
        </div>
    </section>
</template>
